/*
    VUEX Client Module
*/
import Storage from '@/services/storage.service';
import { SESSION_KEYS, MENUS } from '@/common/constants';
import { SET_CLIENT_PROGRESS } from '@/stores/mutations.type';
import ApiService from '../../services/api.service';
import {
  ActionApiRouteMapper,
  RESEND_CLIENT_TEXT,
  RETRY_CLIENT,
  UPDATE_INTERVIEW,
  UPLOAD_CLIENT_DOCUMENT,
  FINISH_INTERVIEW,
  STATEMENT_UPLOAD,
} from '../actions.type';
import { replaceEndpointSlugsFromState } from "../../utils";
import { GenerateInterviewQuestionsArray } from '../../views/client/interview-questions-tree';

// State Data
const state = {
  clientProgress: Storage.get(SESSION_KEYS.CLIENT_PROGRESS) || null,
};

// Getters
const getters = {
  clientProgress(state) {
    return state.clientProgress;
  },
  clientData(state) {
    return state.clientProgress
      ? state.clientProgress.clientData
      : { accounts: [] };
  },
  interviewPages(state) {
    // Guard against cases where there is no stored client progress yet
    if (!state.clientProgress || !state.clientProgress.progress) {
      return [];
    }

    const customQuestionsPresent = state.clientProgress.progress.some(
      (page) => page.pageName === MENUS.CLIENT.CUSTOM_QUESTIONS.id,
    );

    const customQuestionsIds = customQuestionsPresent
      ? state.clientProgress.progress
          .filter((el) => el.pageName === 'custom_questions')
          .map((page) => page.data.question._id)
      : [];

    return new GenerateInterviewQuestionsArray(
      state.clientProgress.clientData,
      state.clientProgress.progress,
      customQuestionsIds,
    );
  }
};

const actions = {
  [RESEND_CLIENT_TEXT]: async ({ rootState }, params) => {
    const organisationId = rootState.auth.currentUser.organisation.id
    const url = ActionApiRouteMapper[RESEND_CLIENT_TEXT]
      .replace(':organisationsId', organisationId)
      .replace(':clientsId', params.clientsId);

    return await ApiService.patch(url, params);
  },
  [RETRY_CLIENT]: async ({ rootState }, clientId) => {
    const organisationId = rootState.auth.currentUser.organisation.id;
    const url = ActionApiRouteMapper[RETRY_CLIENT]
      .replace(':organisationsId', organisationId)
      .replace(':clientsId', clientId);

    return await ApiService.patch(url);
  },
  [UPDATE_INTERVIEW]({ rootState }, payload) {
    const url = replaceEndpointSlugsFromState(
      ActionApiRouteMapper.UPDATE_INTERVIEW,
      rootState,
      'interview',
    );

    return new Promise((resolve, reject) => {
      ApiService.patch(url, payload, UPDATE_INTERVIEW)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  [UPLOAD_CLIENT_DOCUMENT]({ rootState }, payload) {
    const url = replaceEndpointSlugsFromState(
      ActionApiRouteMapper.UPLOAD_CLIENT_DOCUMENT,
      rootState,
      'interview',
    );

    let formData = new FormData();
    formData.append('files', payload.file);
    formData.append('documentName', payload.documentName);
    if (payload.accountId) {
      formData.append('accountId', payload.accountId);
    }
    if (payload.feature) {
      formData.append('feature', payload.feature);
    }

    return new Promise((resolve, reject) => {
      ApiService.put(url, formData)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  [FINISH_INTERVIEW]({ rootState }) {
    const url = replaceEndpointSlugsFromState(
      ActionApiRouteMapper.FINISH_INTERVIEW,
      rootState,
      'interview',
    );

    return new Promise((resolve, reject) => {
      ApiService.post(url, undefined, FINISH_INTERVIEW)
        .then((data) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  [STATEMENT_UPLOAD](_, payload) {
    const url = ActionApiRouteMapper.STATEMENT_UPLOAD.replace(
      ':transitionsId',
      payload.transitionId,
    );

    let formData = new FormData();
    formData.append('statement', payload.file);
    return new Promise((resolve, reject) => {
      ApiService.post(`${url}?relatesTo=${payload.relatesTo}`, formData)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
};

const mutations = {
  /* Set the current user and store in session */
  [SET_CLIENT_PROGRESS](state, progress) {
    if (!state.clientProgress || !progress) {
      state.clientProgress = progress;
    } else {
      const { progress: newInterviewPages } = progress;
      state.clientProgress = {
        ...progress,
        progress: newInterviewPages,
      };
    }
    if (progress === null) {
      state.clientData = null;
      Storage.remove(SESSION_KEYS.CLIENT_PROGRESS);
    } else {
      if (!state.clientProgress.clientData) {
        // Security: Contact PII removed from API
        state.clientProgress.clientData = {
          accounts: [],
        };
        // Build accounts list from beneficiary, decedent, trust, and entity pages
        const relevantPages = state.clientProgress.progress.filter(
          (p) =>
            [
              MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id,
              MENUS.CLIENT.PERSHING_PRIMARY_BENEFICIARIES.id,
              MENUS.CLIENT.INHERITEDIRA.id,
              MENUS.CLIENT.PERSHING_DECEDENT.id,
              MENUS.CLIENT.TRUST_ACCOUNT.id,
              MENUS.CLIENT.ENTITY_ACCOUNT.id,
              MENUS.CLIENT.TRUST_INFO.id,
              MENUS.CLIENT.PERSHING_TRUST_INFO.id,
              MENUS.CLIENT.ENTITY_INFO.id,
            ].includes(p.pageName) && p.instance
        );

        // Add each unique account to the accounts array
        const accountsMap = new Map();
        relevantPages.forEach((p) => {
          const key = `${p.instance}-${p.label}`;
          if (!accountsMap.has(key)) {
            accountsMap.set(key, {
              instance: {
                name: p.instance,
                label: p.label,
              },
            });
          }
        });
        
        // Convert map values to array
        state.clientProgress.clientData.accounts = Array.from(accountsMap.values());
      }
      Storage.set(SESSION_KEYS.CLIENT_PROGRESS, state.clientProgress);
    }
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
